/* eslint-disable eqeqeq */
import {
  useGetRatedPhxQuery,
  useGetTypeCertificationsQuery,
} from 'app/services/ahuComponentFormsApi';
import { COMPONENT_FORM_CONFIGS } from '../componentFormConfigs';
import { formatDecimalDisplay } from '../formUtils';
import { useComponentForm } from '../useComponentForm';
import { FormSubtab, handleTabToNext, type OptionRow } from '../formKit';
import { usePhxFormDeps, type PhxFormDeps } from './usePhxFormDeps';
import {
  BlankOffSlot,
  FormHeading,
  MBareInput,
  MBareSelect,
  MInput,
  MSelect,
  PBareSelect,
  PricingRows,
  RequiredFieldNote,
} from './formCommon';
import type { ComponentFormProps } from './ComponentFormHost';

/**
 * Phase 6e2a + W4 — PLATE HEAT EXCHANGER form (§50.1 row; legacy form.js 409 +
 * form.tmpl 1,726; parity 324). Dual-section.
 *
 * ★ W4 PARKED-MAJOR FIXES (the phx cut):
 *  1. THE DEPENDENCIESHANDLER ENGINE (#324): the ~13 dependent selects
 *     (temp_rating, seal, configuration, mounting, plate/end-plate/corner
 *     mtl+finish+style, plate_orientation, bypass_dmr_mtl/fin) rendered EMPTY
 *     with the saved selected display lost on AHU 2986 — because they were bound
 *     to `componentCms.<path>` (the params doc), which does NOT carry those
 *     option lists. Legacy form.js runs its own DependenciesHandler graph and
 *     loads each list from master/v1.0.0/ahu/plate_heat_exchangers/<filters>
 *     <param>s/. Now wired through `usePhxFormDeps` (reuses the W3/W4
 *     useDependencyGraph reducer + phxParamUrl), which resolves each select's
 *     option list + a selected-label fallback (the loaded row name → the
 *     serializer `<field>_name` companion → the id). Plus restored: the AHRI1060
 *     certification group, the end_plates `Flange` row (model.end_plate_flange),
 *     and the winter `Frosted Surface` rows.
 *  2. THE SEASON-BLOCK DUAL-COLUMN LAYOUT (same class as the EW form fix shipped
 *     in W3): each season renders `li.sublist-heading.two-headings` under SA/EA
 *     ENTERING|LEAVING with the dual-COLUMN rows (entering `a`=oa|ra left,
 *     leaving `b`=sa|ea right, one line per metric). Replaces the prior linear
 *     `label=""` pairs. Mirrors EnergyWheelForm's StreamSubsection exactly.
 *
 * RatedPhxModel fetched at init (§50.1); rating writes = the 6e2c PHX selector
 * cut (patchRatedPhx wired there).
 */
const config = COMPONENT_FORM_CONFIGS.plate_heat_exchanger;

const ESP_FIELDS = [
  'sa_apd_summer',
  'ea_apd_summer',
  'sa_apd_winter',
  'ea_apd_winter',
  'sa_air_stream_summer_id',
  'ea_air_stream_summer_id',
  'sa_air_stream_winter_id',
  'ea_air_stream_winter_id',
];

/**
 * A dependency-graph-backed select (the #324 fix): renders the option list from
 * usePhxFormDeps + ALWAYS includes a synthetic option for the saved id so the
 * selected label/mirror render even before the list loads (the lost-display
 * restore). Bound to the form's model_field commit.
 */
const DepSelect = ({
  api,
  deps,
  name,
  label,
  unit,
}: {
  api: ReturnType<typeof useComponentForm>;
  deps: PhxFormDeps;
  name: string;
  label: string;
  unit?: string;
}) => {
  const value = api.model?.get(name);
  const list = deps.options(name);
  const valStr = value == null || value === '' ? '' : String(value);
  const inList = list.some((r) => String(r.id ?? (r as any).value) === valStr);
  // synthesize the saved option when it's not in the loaded list (so the value
  // and its label always render — the restored selected display).
  const options: OptionRow[] =
    valStr !== '' && !inList
      ? [{ id: value as number, name: deps.selectedLabel(name, value) }, ...list]
      : list;
  return (
    <MSelect
      api={api}
      name={name}
      label={label}
      unit={unit}
      options={options}
      onChangeExtra={() => deps.onParentChange(name)}
    />
  );
};

/**
 * One season sub-section — the dual-COLUMN layout (the parked MAJOR #2). Mirrors
 * EnergyWheelForm's StreamSubsection: a `li.sublist-heading.two-headings` +
 * dual-input rows where the ENTERING side (`a` = oa|ra) is the left input and
 * the LEAVING side (`b` = sa|ea) is the right input, ON ONE LINE per metric.
 * heatex (mfg 52) → WB temp; recuperator (167) → RH (the per-mfg WB/RH toggle —
 * legacy ifCompare model.master_phx.mfg_id != 52).
 */
const PhxStreamSubsection = ({
  api,
  a,
  b,
  season,
  streams,
  enteringHeading,
  leavingHeading,
  isHeatex,
}: {
  api: ReturnType<typeof useComponentForm>;
  a: string;
  b: string;
  season: 'summer' | 'winter';
  streams: OptionRow[];
  enteringHeading: string;
  leavingHeading: string;
  isHeatex: boolean;
}) => (
  <>
    <li className="sublist-heading two-headings">
      <h3>{enteringHeading} </h3>
      <h3>{leavingHeading}</h3>
    </li>
    {/* Air Stream (entering | leaving) */}
    <li>
      <div className="single-line er-wheel">
        <MBareSelect api={api} name={`${a}_air_stream_${season}_id`} options={streams} extraClass="medium-input" />
        <MBareSelect api={api} name={`${b}_air_stream_${season}_id`} options={streams} extraClass="medium-input right-input" />
        <span className="label">Air Stream</span>
      </div>
    </li>
    {/* Airflow (entering | leaving) */}
    <li>
      <div className="first-two-line">
        <span className="unit">CFM</span>
        <MBareInput api={api} name={`${a}_airflow_actual_${season}`} formatter="integer" extraClass="medium-input integer" />
        <MBareInput api={api} name={`${b}_airflow_actual_${season}`} formatter="integer" extraClass="medium-input integer" />
        <span className="label">Airflow</span>
      </div>
    </li>
    {/* DB Temperature (entering | leaving) */}
    <li>
      <div className="gas-heater-total-airflow">
        <span className="unit temp"> F </span>
        <MBareInput api={api} name={`${a}_db_temp_${season}`} extraClass="medium-input" />
        <MBareInput api={api} name={`${b}_db_temp_${season}`} extraClass="medium-input" />
        <span className="label">DB Temperature</span>
      </div>
    </li>
    {/* Humidity (%) — recuperator only */}
    {!isHeatex && (
      <li>
        <div>
          <span className="unit">%</span>
          <MBareInput api={api} name={`${a}_rh_${season}`} extraClass="medium-input integer" />
          <MBareInput api={api} name={`${b}_rh_${season}`} extraClass="medium-input integer" />
          <span className="label">Humidity</span>
        </div>
      </li>
    )}
    {/* WB Temperature (F) — heatex only */}
    {isHeatex && (
      <li>
        <div className="gas-heater-total-airflow">
          <span className="unit temp"> F </span>
          <MBareInput api={api} name={`${a}_wb_temp_${season}`} extraClass="medium-input" />
          <MBareInput api={api} name={`${b}_wb_temp_${season}`} extraClass="medium-input" />
          <span className="label">WB Temperature</span>
        </div>
      </li>
    )}
    {/* Face Velocity (leaving side, readonly) */}
    <li className="right-column">
      <div className="multiply-total">
        <span className="unit">fpm</span>
        <MBareInput api={api} name={`${b}_face_velocity_${season}`} formatter="integer" extraClass="medium-input integer" readOnly />
        <span className="label">Face Velocity</span>
      </div>
    </li>
    {/* APD (leaving side) */}
    <li className="right-column">
      <div className="multiply-total">
        <span className="unit"><span>in</span> <span>WC</span></span>
        <MBareInput api={api} name={`${b}_apd_${season}`} extraClass="medium-input integer" />
        <span className="label">APD</span>
      </div>
    </li>
    {/* Condensate Rate (leaving side) */}
    <li className="right-column">
      <div className="multiply-total">
        <span className="unit">gph</span>
        <MBareInput api={api} name={`${b}_condensation_rate_${season}`} extraClass="medium-input integer" />
        <span className="label">Condensate Rate</span>
      </div>
    </li>
    {/* Frosted Surface (leaving side) — winter only (legacy 890 / 1060). */}
    {season === 'winter' && (
      <li className="right-column">
        <div className="multiply-total">
          <span className="unit">%</span>
          <MBareInput api={api} name={`${b}_frost_surface_percent_winter`} extraClass="medium-input integer" />
          <span className="label">Frosted Surface</span>
        </div>
      </li>
    )}
  </>
);

const SeasonTotals = ({
  api,
  season,
}: {
  api: ReturnType<typeof useComponentForm>;
  season: 'summer' | 'winter';
}) => (
  <>
    <MInput api={api} name={`wet_temp_effectiveness_${season}`} label="Wet Temperature Effectiveness" unit="%" />
    <MInput api={api} name={`dry_temp_effectiveness_${season}`} label="Dry Temperature Effectiveness" unit="%" />
    <MInput api={api} name={`wet_temp_ratio_${season}`} label="Wet Temperature Ratio" unit="%" />
    <MInput api={api} name={`dry_temp_ratio_${season}`} label="Dry Temperature Ratio" unit="%" />
    {season === 'winter' && (
      <MInput api={api} name="frost_starting_temp_winter" label="Frost Starting Temperature" unit="oF" />
    )}
    <MInput api={api} name={`efficiency_${season}_ashrae`} label="Efficiency" unit="%" />
    <MInput api={api} name={`hrs_capacity_${season}`} label="Total" unit="btu/hr" />
    <MInput api={api} name={`erp_1253_2014_${season}`} label="ERP 2053/2014" unit="%" />
  </>
);

const PlateHeatExchangerForm = ({ row, section, section2, cms }: ComponentFormProps) => {
  const api = useComponentForm({
    config,
    row,
    section,
    section2,
    firstSubtab: 'properties',
    espInfoFields: ESP_FIELDS,
  });
  const { model } = api;
  // RatedPhxModel init fetch (§50.1) — read side; rating writes = 6e2c.
  useGetRatedPhxQuery(row.id);
  // THE FORM DEPENDENCY ENGINE (the #324 fix) — loads the master-list option
  // lists + resolves the lost selected labels. Read-only (no writes).
  const deps = usePhxFormDeps(model);
  // certifications (the AHRI1060 group): the component detail's `certification`
  // field is NULL — the selected certs live on the /certifications/ relation
  // endpoint (multiCheckbox ahuphxcertification, like the EW form). Read-only.
  const { data: phxCerts } = useGetTypeCertificationsQuery({
    plural: 'plate_heat_exchangers',
    id: row.id,
  });

  if (api.loading || !model) {
    return <div className="preloader dotted-preloader active" />;
  }
  const sub = (name: string) => !!api.subtabs[name];
  const masterPhx = (model.get('master_phx') ?? {}) as Record<string, unknown>;
  const isHeatex = Number((masterPhx as any).mfg_id) == 52;
  const streams = (((cms as Record<string, unknown> | undefined)?.air as
    | { air_stream?: OptionRow[] }
    | undefined)?.air_stream ?? []) as OptionRow[];
  // certifications: the selected certs (the AHRI1060 group) come from the
  // /certifications/ relation endpoint (the component detail's `certification`
  // field is null). Renders these as checked checkboxes + the output echo.
  const certs = ((phxCerts as OptionRow[] | undefined) ?? []) as OptionRow[];

  return (
    <>
      <FormHeading
        title="Heat Exchanger"
        api={api}
        wrapperClass="component-form-heading clearfix hide"
        chrome={[
          {
            field: config.catalogueFields[0],
            visClass: 'vis-silencer',
            buttonExtraClass: 'hide rating',
            showSmartlist: false,
            searchResultClass: 'search-result',
          },
        ]}
        withRatingBlock
        ratingBlockDataName="plate_heat_exchanger"
      />
      {/* Both legacy hooks, back where form.tmpl:15-16 had them: the FORM class
          drives ahu/styles.css:1682-1713 (whole-form lock, plus the
          height_from_floor / dist_from_left exceptions the conditional
          `non-clickable` below relies on); the UL id drives :4939-4973. */}
      <form className="plate_heat_exchanger rating-mode" onKeyDown={handleTabToNext}>
        <ul className="inputs-group rating-mode max-height-auto" id="plate-heat-exchanger-form">
          <FormSubtab type="plate_heat_exchanger" cid={row.id} name="properties" label="Properties" open={sub('properties')} onToggle={api.toggleSubtab}>
            {/* read-only master_phx lines (set by the selector, not these rows) */}
            <li>
              <div>
                <span className="rating-selection"><span>{String(masterPhx.mfg ?? '')}</span></span>
                <span className="label ">Manufacturer</span>
              </div>
            </li>
            <li>
              <div>
                <span className="rating-selection"><span>{String(masterPhx.series ?? '')}</span></span>
                <span className="label ">Series</span>
              </div>
            </li>
            <li>
              <div className="phx_model_div">
                <input className="model_field long-input non-clickable" type="text" autoComplete="off" name="model" value={String(model.get('model') ?? '')} readOnly tabIndex={-1} onChange={() => undefined} />
                <span className="label">Model</span>
              </div>
            </li>
            <li>
              <div>
                <span className="rating-selection"><span>{String(masterPhx.size ?? '')}</span></span>
                <span className="label ">Size</span>
              </div>
            </li>
            <li>
              <div>
                <input className="plate_heat_exchanger_params_field non-clickable" type="text" autoComplete="off" name="shape_id" value={String(masterPhx.shape ?? '')} readOnly tabIndex={-1} onChange={() => undefined} />
                <span className="label ">Shape</span>
              </div>
            </li>
            <li>
              <div>
                <span className="rating-selection"><span>{String(masterPhx.flow_type ?? '')}</span></span>
                <span className="label ">Flow</span>
              </div>
            </li>
            {/* THE DEPS-ENGINE SELECTS (the #324 fix — now resolve options +
                selected labels from the dependency master lists). */}
            <DepSelect api={api} deps={deps} name="temp_rating_id" label="Temperature Rating" unit="oF" />
            <DepSelect api={api} deps={deps} name="seal_id" label="Seal" />
            <MInput api={api} name="min_desired_efficiency" label="Min Requested Efficiency" unit="%" formatter="none" />
            <DepSelect api={api} deps={deps} name="configuration_id" label="Configuration" />
            <DepSelect api={api} deps={deps} name="mounting_id" label="Mounting" />
            {/* certification group (the AHRI1060 cert group — restored). */}
            <li className="certification-group">
              <div>
                <button id="show_phx_certifications" type="button" className="icon-selection" tabIndex={-1} />
                <div className="certification-edit-list">
                  <ul>
                    {certs.map((cert) => (
                      <li key={String(cert.id)}>
                        <label>
                          <input type="checkbox" value={String(cert.id)} className="certification" name="certification" defaultChecked readOnly />
                          <span className="label">{String(cert.name ?? '')}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
                {/* ★ One `<span>` PER selected certification, no separator text and no
                    "None" placeholder — that is exactly what the legacy multiCheckbox
                    writes into this element (multi_checkbox_selector.js:105-116 clears
                    innerHTML then concatenates "<span>" + outputSerializer(model) +
                    "</span>", so an empty collection leaves the element empty). The
                    value's styling hangs off that child span:
                    `.certification-group .checked-certifications span { font-size: 13px;
                    color: #212121 }` (common.css:20749, and ahu/styles.css:2870 under
                    `.inputs-group .sublist`). The port emitted a bare text node via
                    `.join(', ')`, so neither rule matched and AHRI1060 inherited the
                    row's font size and colour instead. The ", " between values is CSS
                    too (`span:not(:empty) + span:not(:empty):before`, common.css:20753,
                    with `margin-left: -0.25em`) — joining in JS both double-spaces it
                    and keeps the spans from existing at all. Same fix already shipped
                    for the rating dialog in PlateHeatExchangerSelector.tsx. */}
                <span className="checked-certifications output certification_output phx-certifications">
                  {certs
                    .map((c) => [String(c.id), String(c.name ?? '')] as const)
                    .filter(([, name]) => Boolean(name))
                    .map(([cid, name]) => <span key={cid}>{name}</span>)}
                </span>
                <span className="label">Certification</span>
              </div>
            </li>
            <li>
              <div className="coil-qty-fpi multiply">
                <MBareInput api={api} name="bank_wide" formatter="integer" extraClass="small-input" />
                <span className="multiply-sign">X</span>
                <MBareInput api={api} name="bank_high" formatter="integer" extraClass="small-input" />
                <span className="multiply-sign">X</span>
                <MBareInput api={api} name="bank_deep" formatter="integer" extraClass="small-input" />
                <span className="label">
                  <span>Bank Wide</span> x <span>High</span> x <span>Deep</span>
                </span>
              </div>
            </li>
            <li>
              <div className="coil-qty-fpi multiply">
                <MBareInput api={api} name="casing_width" extraClass="small-input" />
                <span className="multiply-sign">x</span>
                {model.get('casing_height') != null ? (
                  <MBareInput api={api} name="casing_height" extraClass="small-input" />
                ) : (
                  <input type="text" data-formatter_type="decimal" autoComplete="off" name="casing_height" className="model_field small-input" value={formatDecimalDisplay(masterPhx.casing_height)} readOnly onChange={() => undefined} />
                )}
                <span className="multiply-sign">x</span>
                {model.get('casing_depth') != null ? (
                  <MBareInput api={api} name="casing_depth" extraClass="small-input" />
                ) : (
                  <input type="text" data-formatter_type="decimal" autoComplete="off" name="casing_depth" className="model_field small-input" value={formatDecimalDisplay(masterPhx.casing_depth)} readOnly onChange={() => undefined} />
                )}
                <span className="label">
                  <span>Overall W</span> x <span>H</span> x <span>D</span>
                </span>
              </div>
            </li>
            <li>
              <div className="">
                <span className="unit">in</span>
                <MBareInput
                  api={api}
                  name="height_from_floor"
                  extraClass={`medium-input${model.get('check_height_from_floor') ? '' : ' non-clickable'}`}
                />
                <MBareInput
                  api={api}
                  name="dist_from_left"
                  extraClass={`medium-input${model.get('check_dist_from_left') ? '' : ' non-clickable'}`}
                />
                <span className="label">Distance from : Floor | Left</span>
              </div>
            </li>
            <li>
              <div className="">
                <span className="unit">in</span>
                <MBareSelect api={api} name="face_one_definition_id" cmsPath="face_definition" extraClass="medium-input" />
                <span className="unit">in</span>
                <MBareSelect api={api} name="face_two_definition_id" cmsPath="face_definition" extraClass="medium-input" />
                <span className="label">Face Definition 1 / 2</span>
              </div>
            </li>
          </FormSubtab>

          <FormSubtab type="plate_heat_exchanger" cid={row.id} name="internal_plates" label="Internal Plates" open={sub('internal_plates')} onToggle={api.toggleSubtab}>
            <li>
              <div className="single-line">
                <PBareSelect api={api} target="master_phx_id" name="plate_spacing_code_id" cmsPath="spacing_code" extraClass="medium-input" />
                {model.get('plate_spacing') != null ? (
                  <MBareInput api={api} name="plate_spacing" extraClass="medium-input" />
                ) : (
                  <input type="text" data-formatter_type="decimal" autoComplete="off" name="plate_spacing" className="model_field medium-input" value={formatDecimalDisplay(masterPhx.plate_spacing)} readOnly onChange={() => undefined} />
                )}
                <span className="label">Spacing</span>
              </div>
            </li>
            <DepSelect api={api} deps={deps} name="plate_orientation_id" label="Orientation" />
            <DepSelect api={api} deps={deps} name="plate_mtl_id" label="Material" />
            <DepSelect api={api} deps={deps} name="plate_finish_id" label="Finish" />
          </FormSubtab>

          <FormSubtab type="plate_heat_exchanger" cid={row.id} name="end_plates" label="End Plates" open={sub('end_plates')} onToggle={api.toggleSubtab}>
            <DepSelect api={api} deps={deps} name="end_plate_style_id" label="Style" />
            {/* the end_plates Flange row (restored — model.end_plate_flange). */}
            <li>
              <div className="single-line">
                <input type="text" name="end_plate_flange" className="non-clickable" value={String(model.get('end_plate_flange') ?? '')} readOnly tabIndex={-1} onChange={() => undefined} />
                <span className="label">Flange</span>
              </div>
            </li>
            <DepSelect api={api} deps={deps} name="end_plate_mtl_id" label="Material" />
            <DepSelect api={api} deps={deps} name="end_plate_finish_id" label="Finish" />
          </FormSubtab>

          <FormSubtab type="plate_heat_exchanger" cid={row.id} name="corner_profiles" label="Corner Profiles" open={sub('corner_profiles')} onToggle={api.toggleSubtab}>
            <DepSelect api={api} deps={deps} name="corner_profile_style_id" label="Style" />
            <DepSelect api={api} deps={deps} name="corner_profile_mtl_id" label="Material" />
            <DepSelect api={api} deps={deps} name="corner_profile_finish_id" label="Finish" />
          </FormSubtab>

          <FormSubtab type="plate_heat_exchanger" cid={row.id} name="bypass" label="Bypass" open={sub('bypass')} onToggle={api.toggleSubtab}>
            <MSelect api={api} name="bypass_location_id" label="Location" cmsPath="bypass.location" />
            <MSelect api={api} name="bypass_type_id" label="Type" cmsPath="bypass.type" />
            <MSelect api={api} name="bypass_side_id" label="Side" cmsPath="bypass.dmr_side" />
            <MInput api={api} name="bypass_width" label="Width" unit="in" />
            <MSelect api={api} name="bypass_dmr_config_id" label="Configuration" cmsPath="bypass.dmr_config" />
            <MSelect api={api} name="bypass_dmr_location_id" label="Location" cmsPath="bypass.dmr_location" />
            <MSelect api={api} name="bypass_dmr_drive_location_id" label="Drive Shaft Location" cmsPath="bypass.dmr_drive_location" />
            <DepSelect api={api} deps={deps} name="bypass_dmr_mtl_id" label="Material" />
            <DepSelect api={api} deps={deps} name="bypass_dmr_fin_id" label="Finish" />
            <MSelect api={api} name="dmrs_supplied_by_id" label="Supplied by" cmsPath="bypass.dmrs_supplied_by" />
          </FormSubtab>

          <FormSubtab type="plate_heat_exchanger" cid={row.id} name="recirculation" label="Recirculation" open={sub('recirculation')} onToggle={api.toggleSubtab}>
            <MSelect api={api} name="recirc_dmr_config_id" label="Configuration" cmsPath="recirc_dmr.config" />
            <MSelect api={api} name="recirc_dmr_location_id" label="Location" cmsPath="recirc_dmr.location" />
          </FormSubtab>

          <FormSubtab type="plate_heat_exchanger" cid={row.id} name="performance_winter" label="Performance : Winter" open={sub('performance_winter')} onToggle={api.toggleSubtab}>
            <PhxStreamSubsection api={api} a="oa" b="sa" season="winter" streams={streams}
              enteringHeading="SA ENTERING" leavingHeading="SA LEAVING" isHeatex={isHeatex} />
            <MInput api={api} name="oa_sp" label="SP Outside" unit="in WC" />
            <PhxStreamSubsection api={api} a="ra" b="ea" season="winter" streams={streams}
              enteringHeading="EA ENTERING" leavingHeading="EA LEAVING" isHeatex={isHeatex} />
            <MInput api={api} name="condensate" label="Condensate" unit="gpm" />
            <SeasonTotals api={api} season="winter" />
          </FormSubtab>

          <FormSubtab type="plate_heat_exchanger" cid={row.id} name="performance_summer" label="Performance : Summer" open={sub('performance_summer')} onToggle={api.toggleSubtab}>
            <PhxStreamSubsection api={api} a="oa" b="sa" season="summer" streams={streams}
              enteringHeading="SA ENTERING" leavingHeading="SA LEAVING" isHeatex={isHeatex} />
            <MInput api={api} name="oa_sp" label="SP Outside" unit="in WC" />
            <PhxStreamSubsection api={api} a="ra" b="ea" season="summer" streams={streams}
              enteringHeading="EA ENTERING" leavingHeading="EA LEAVING" isHeatex={isHeatex} />
            <SeasonTotals api={api} season="summer" />
          </FormSubtab>

          <FormSubtab type="plate_heat_exchanger" cid={row.id} name="pricing" label="Pricing" open={sub('pricing')} onToggle={api.toggleSubtab}>
            <PricingRows api={api} label="PHX" />
          </FormSubtab>

          <BlankOffSlot api={api} sectionId={section.id} />
        </ul>
      </form>
      {/* components/plate_heat_exchanger/tmpl/form.tmpl — the footer note closes every legacy
          component form; this port dropped it (same omission as the coil
          panel this branch fixes). */}
      <RequiredFieldNote />
    </>
  );
};

export default PlateHeatExchangerForm;
